import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { message, Modal } from "antd";
import { selectUser } from "../../redux/slices/user";
import SetmenuUI from "./components/SetmenuUI";
import {
  disableSetmenuOptionAdmin,
  getSetmenuGroups,
  getSetmenuOptionsByGroup,
  updateSetmenusOrder,
  getSetMenuCustomizableChannels,
  updateSetMenuCustomizableChannels,
} from "../../api/Products";

const Setmenus = () => {
  const [loading, handleLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [condiments, setCondiments] = useState([]); // Mantenemos el nombre 'condiments' para compatibilidad con la estructura UI
  const [condimentsLoading, setCondimentsLoading] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState(null);

  const user = useSelector(selectUser);
  const [visibleEditor, handleVisibleEditor] = useState(false);

  const isSucursal = false; // Los setmenus solo se administran centralmente
  const isAdmin = user.admin === 1;

  // Estados para configuración de canales personalizables
  const [customizableChannels, setCustomizableChannels] = useState([]);
  const [loadingCustomizable, setLoadingCustomizable] = useState(false);
  const [savingCustomizable, setSavingCustomizable] = useState(false);

  const fetchCustomizableChannelsList = async (groupId) => {
    const targetGroupId = groupId || selectedGroupId;
    if (!targetGroupId) return;
    setLoadingCustomizable(true);
    const response = await getSetMenuCustomizableChannels(targetGroupId);
    if (response && response.data && response.data.status === "success") {
      setCustomizableChannels(response.data.channels || []);
    } else {
      message.error("Error al cargar la configuración de canales");
    }
    setLoadingCustomizable(false);
  };

  const handleSaveCustomizableChannels = async (channelIds) => {
    if (!selectedGroupId) return false;
    setSavingCustomizable(true);
    const response = await updateSetMenuCustomizableChannels(selectedGroupId, channelIds, user.id);
    let success = false;
    if (response && response.data && response.data.status === "success") {
      message.success("Configuración de canales guardada con éxito");
      fetchCondiments(selectedGroupId);
      success = true;
    } else {
      message.error("Error al guardar la configuración de canales");
    }
    setSavingCustomizable(false);
    return success;
  };

  // ----- Obtener grupos de setmenus -----
  const fetchGroups = useCallback(async () => {
    handleLoading(true);
    try {
      const response = await getSetmenuGroups();
      if (!response.error && response.status === 200 && response.data?.status === "success") {
        const g = response.data.groups || [];
        setGroups(g);
        // Autoseleccionar primer grupo
        if (g.length > 0 && !selectedGroupId) {
          setSelectedGroupId(g[0].id);
        }
      } else {
        message.error("¡Hubo un problema cargando los combos!");
      }
    } catch (e) {
      message.error("¡Hubo un problema!");
    } finally {
      handleLoading(false);
    }
  }, [selectedGroupId]);

  // ----- Obtener opciones del setmenu seleccionado -----
  const fetchCondiments = useCallback(async (groupId) => {
    if (!groupId) return;
    setCondiments([]);
    setCondimentsLoading(true);
    try {
      const response = await getSetmenuOptionsByGroup(groupId);
      if (!response.error && response.status === 200 && response.data?.status === "success") {
        setCondiments(response.data.condiments || []);
      } else {
        message.error("¡Hubo un problema cargando las opciones de combo!");
      }
    } catch (e) {
      message.error("¡Hubo un problema!");
    } finally {
      setCondimentsLoading(false);
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Al cambiar grupo, cargar opciones
  useEffect(() => {
    if (selectedGroupId) {
      setSelectedChannelId(null);
      fetchCondiments(selectedGroupId);
    }
  }, [selectedGroupId, fetchCondiments]);

  const handleGroupSelect = (groupId) => {
    setSelectedGroupId(groupId);
  };

  const refreshCurrent = () => {
    fetchCondiments(selectedGroupId);
  };

  const confirmVisibility = (sku, mode, active, groupId) => {
    Modal.confirm({
      title: `¿${active ? 'Desactivar' : 'Activar'} para este canal?`,
      okText: "Si",
      cancelText: "No",
      async onOk() {
        try {
          const response = await disableSetmenuOptionAdmin(user.id, sku, mode, groupId);
          if (response.error || response.data?.status !== "success") {
            message.error("¡Hubo un problema!");
          }
        } catch (e) {
          message.error("¡Hubo un problema!");
        } finally {
          refreshCurrent();
        }
      },
      onCancel() { },
    });
  };

  const sortedCondiments = [...condiments].sort((a, b) => {
    if (isAdmin && selectedChannelId) {
      const aChan = a.channels?.find(ch => ch.channel_id === selectedChannelId);
      const bChan = b.channels?.find(ch => ch.channel_id === selectedChannelId);
      const aOrder = aChan ? aChan.setOrder : a.setOrder;
      const bOrder = bChan ? bChan.setOrder : b.setOrder;
      return (aOrder || 0) - (bOrder || 0);
    }
    return (a.setOrder || 0) - (b.setOrder || 0);
  });

  const handleDragDropSku = async (draggedSku, targetSku) => {
    const dragIdx = sortedCondiments.findIndex(c => c.sku === draggedSku);
    const targetIdx = sortedCondiments.findIndex(c => c.sku === targetSku);
    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) return;

    const list = [...sortedCondiments];
    const [draggedItem] = list.splice(dragIdx, 1);
    list.splice(targetIdx, 0, draggedItem);

    // Mapear nuevos órdenes
    const orderList = list.map((item, idx) => ({
      sku: item.sku,
      setOrder: idx
    }));

    // Actualizar estado local
    const updatedCondiments = condiments.map(c => {
      const orderItem = orderList.find(x => x.sku === c.sku);
      if (orderItem) {
        return {
          ...c,
          channels: c.channels.map(ch => ch.channel_id === selectedChannelId ? { ...ch, setOrder: orderItem.setOrder } : ch)
        };
      }
      return c;
    });

    setCondiments(updatedCondiments);

    // Llamar a API
    try {
      const res = await updateSetmenusOrder(selectedGroupId, selectedChannelId, orderList, user.id);
      if (res.error || !res.data || res.data.status !== "success") {
        message.error("¡Hubo un problema actualizando el orden!");
        fetchCondiments(selectedGroupId);
      } else {
        message.success("¡Orden actualizado!");
        fetchCondiments(selectedGroupId);
      }
    } catch (e) {
      message.error("¡Hubo un problema actualizando el orden!");
      fetchCondiments(selectedGroupId);
    }
  };

  const closeEditor = () => handleVisibleEditor(false);

  return (
    <SetmenuUI
      loading={loading}
      condimentsLoading={condimentsLoading}
      groups={groups}
      condiments={sortedCondiments}
      selectedGroupId={selectedGroupId}
      onGroupSelect={handleGroupSelect}
      confirmVisibility={confirmVisibility}
      visibleEditor={visibleEditor}
      closeEditor={closeEditor}
      isSucursal={isSucursal}
      selectedChannelId={selectedChannelId}
      onChannelChange={setSelectedChannelId}
      handleDragDropSku={handleDragDropSku}
      customizableChannels={customizableChannels}
      loadingCustomizable={loadingCustomizable}
      savingCustomizable={savingCustomizable}
      fetchCustomizableChannels={fetchCustomizableChannelsList}
      onSaveCustomizableChannels={handleSaveCustomizableChannels}
    />
  );
};

export default Setmenus;
